import { msg } from "@lingui/core/macro";
import { createMemo, For, Match, Show, Switch } from "solid-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/providers/i18n-provider";
import { api, createApiQuery } from "@/lib/api";
import { parseUserAgent, sharedApi } from "@space/shared";
import {
  AlertCircle,
  Laptop,
  Monitor,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  XCircle,
} from "lucide-solid";
import { useSignOut } from "@/providers/boot-manager";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createExtendedPowerSyncStatus } from "@space/shared/solid";

interface SessionsSectionProps {
  currentSessionId: string | undefined;
}

export function SessionsSection(props: SessionsSectionProps) {
  const { t } = useI18n();
  const ctx = usePowerSyncContext();
  const status = createExtendedPowerSyncStatus(() => ctx.db());
  const { promptRevokeSession, isSigningOut } = useSignOut();

  const sessionsQuery = createApiQuery(
    () => ["active-sessions", props.currentSessionId],
    async () => sharedApi.listSessions(api),
    () => ({
      enabled: Boolean(props.currentSessionId),
      refetchOnWindowFocus: true,
      suspense: false,
    }),
  );

  const sessions = createMemo(() => {
    // ... same session mapping logic ...
    if (!sessionsQuery.data) {
      return [];
    }
    return sessionsQuery.data.data.map((s) => ({
      ...s,
      isCurrent: s.id === props.currentSessionId,
    }));
  });

  return (
    <Card>
      <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
        <div class="flex items-center gap-2">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Active sessions`)}</CardTitle>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void sessionsQuery.refetch()}
          disabled={sessionsQuery.isRefetching || !status().isOnline}
          class="text-xs text-muted-foreground"
        >
          <RefreshCw class={`mr-2 h-3 w-3 ${sessionsQuery.isRefetching ? "animate-spin" : ""}`} />
          {t(msg`Refresh`)}
        </Button>
      </CardHeader>
      <CardContent class="space-y-4">
        <Show when={!status().isOnline}>
          <div class="flex items-start gap-3 rounded-xl border border-warning/20 bg-warning/5 p-3">
            <AlertCircle class="mt-0.5 h-4 w-4 text-warning" />
            <div class="space-y-1">
              <p class="text-sm font-bold text-warning">{t(msg`Offline`)}</p>
              <p class="text-xs text-muted-foreground">
                {t(msg`Connect to the internet to see and manage your active sessions.`)}
              </p>
            </div>
          </div>
        </Show>

        <Show
          when={!sessionsQuery.isPending}
          fallback={
            <div class="space-y-3">
              <Skeleton class="h-16 w-full rounded-xl" />
              <Skeleton class="h-16 w-full rounded-xl" />
            </div>
          }
        >
          <div class="grid gap-3">
            <For
              each={sessions()}
              fallback={
                <p class="py-4 text-center text-sm italic text-muted-foreground">
                  {t(msg`No active sessions found.`)}
                </p>
              }
            >
              {(session) => (
                <div
                  class={`flex items-center gap-4 rounded-xl border p-4 transition-all ${
                    session.isCurrent
                      ? "border-primary/20 bg-primary/5"
                      : "border-border bg-muted/30"
                  }`}
                >
                  <div
                    class={`flex h-10 w-10 items-center justify-center rounded-full ${
                      session.isCurrent
                        ? "bg-primary/20 text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    <DeviceIcon userAgent={session.userAgent} />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold truncate">
                      {parseUserAgent(session.userAgent, (label) => t(label))}
                    </p>
                    <div class="mt-1 flex items-center gap-2">
                      <p class="text-xs text-muted-foreground">
                        {t(msg`Started`)}: {new Date(session.createdAt).toLocaleDateString()}
                      </p>
                      <Show when={session.isCurrent}>
                        <Badge
                          variant="secondary"
                          class="h-5 px-1.5 text-[10px] font-bold uppercase tracking-wider bg-primary/20 text-primary border-none"
                        >
                          {t(msg`This device`)}
                        </Badge>
                      </Show>
                    </div>
                  </div>
                  <Show when={!session.isCurrent}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        promptRevokeSession(session.id);
                      }}
                      disabled={isSigningOut()}
                      aria-label={t(msg`Revoke session`)}
                      data-testid="revoke-session-button"
                      class="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <XCircle class="h-5 w-5" />
                    </Button>
                  </Show>
                </div>
              )}
            </For>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}

function DeviceIcon(props: { userAgent: string }) {
  const ua = createMemo(() => props.userAgent.toLowerCase());
  return (
    <Switch fallback={<Laptop class="h-5 w-5" />}>
      <Match when={ua().includes("mobi") || ua().includes("iphone") || ua().includes("android")}>
        <Smartphone class="h-5 w-5" />
      </Match>
      <Match when={ua().includes("tablet") || ua().includes("ipad")}>
        <Monitor class="h-5 w-5" />
      </Match>
    </Switch>
  );
}
